import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Buildings, Globe, Trash, Users,
  X, ShieldCheck, Archive, ArrowCounterClockwise, Prohibit, CheckCircle, Phone, EnvelopeSimple, DownloadSimple, SortAscending, Ticket, ArrowLeft, ChatCircleDots, Star
} from "@phosphor-icons/react";
import { formatLocal, formatLocalDate } from "@/lib/timezone";
import { clientDisplayName } from "@/lib/clientDisplay";
import ClientLogo from "@/components/ClientLogo";

function ExpiryBadge({ expiryDate }) {
  if (!expiryDate) return null;
  const d = new Date(expiryDate);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const msPerDay = 86400000;
  const daysUntil = Math.floor((d.getTime() - now.getTime()) / msPerDay);
  const label = d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  // Red for expired OR expiring within the next 45 days — these are the
  // clients the renewal team needs to act on this week.
  let cls = "bg-[#F5F5F5] text-[#525252]";
  let text = `Expires ${label}`;
  if (daysUntil < 0) {
    cls = "bg-[#FEF2F2] text-[#DC2626]";
    text = `Expired ${label}`;
  } else if (daysUntil <= 45) {
    cls = "bg-[#FEF2F2] text-[#DC2626]";
    text = `Expires in ${daysUntil}d · ${label}`;
  } else if (daysUntil <= 90) {
    cls = "bg-[#FFFBEB] text-[#D97706]";
  }
  return (
    <span
      className={`text-[10px] font-bold uppercase px-1.5 py-0.5 whitespace-nowrap ${cls}`}
      title={`Expires ${label}`}
      data-testid="client-expiry-badge"
    >
      {text}
    </span>
  );
}

export default function ClientsPage() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("active");
  const [showCreate, setShowCreate] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [syncPrompt, setSyncPrompt] = useState(null);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState({ name: "", approved_email_domains: [], company_address: "", city: "", state_region: "", postal_code: "", country: "", contact_first_name: "", contact_last_name: "", contact_email: "", contact_phone: "", finops_client_id: "" });
  const [domainInput, setDomainInput] = useState("");
  const [ticketsTenant, setTicketsTenant] = useState(null);
  const [tenantTickets, setTenantTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const loadTenantTickets = async (tenant) => {
    setTicketsTenant(tenant);
    setSelectedTicket(null);
    setTicketsLoading(true);
    try {
      const res = await api.get(`/support/tickets?tenant_id=${tenant.id}&status=all`);
      setTenantTickets(res.data);
    } catch (err) { toast.error("Failed to load tickets"); setTenantTickets([]); }
    finally { setTicketsLoading(false); }
  };

  const loadTicketDetail = async (ticketId) => {
    try {
      const res = await api.get(`/support/tickets/${ticketId}`);
      setSelectedTicket(res.data);
    } catch (err) { toast.error("Failed to load ticket"); }
  };

  // `loadFinopsClients` used to run here too. It fetched the whole
  // FinOps client list on every page load, stored it, passed it to
  // ClientForm as a prop — and ClientForm never read it. The form
  // searches on demand instead (see `searchFinops`). So it was a
  // cross-database query into the finance system on every visit to
  // this screen, for nothing.
  useEffect(() => { loadTenants(); }, []);

  const loadTenants = async () => {
    try {
      const res = await api.get("/tenants");
      setTenants(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleAddDomain = () => {
    const d = domainInput.trim().toLowerCase().replace(/^@/, "");
    if (!d) return;
    if (form.approved_email_domains.includes(d)) return toast.error("Domain already added");
    if (!d.includes(".")) return toast.error("Enter a valid domain (e.g. stanford.edu)");
    setForm({ ...form, approved_email_domains: [...form.approved_email_domains, d] });
    setDomainInput("");
  };

  const handleRemoveDomain = (domain) => { setForm({ ...form, approved_email_domains: form.approved_email_domains.filter(d => d !== domain) }); };
  const handleDomainKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); handleAddDomain(); } };

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    if (form.approved_email_domains.length === 0) return toast.error("At least one email domain is required");
    try {
      await api.post("/tenants", form);
      toast.success(`Client "${form.name}" created`);
      setShowCreate(false); resetForm(); loadTenants();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create client"); }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    // Session 60 — auto-split legacy `contact_name` into first/last when the
    // split columns are empty (mirrors the backend fallback so freshly-opened
    // tenants with pre-split data don't show blank name inputs).
    const parts = (tenant.contact_name || "").trim().split(/\s+/);
    const fallbackFirst = parts[0] || "";
    const fallbackLast = parts.slice(1).join(" ") || "";
    setForm({
      name: tenant.name,
      approved_email_domains: [...tenant.approved_email_domains],
      company_address: tenant.company_address || "",
      city: tenant.city || "",
      state_region: tenant.state_region || "",
      postal_code: tenant.postal_code || "",
      country: tenant.country || "",
      contact_first_name: tenant.contact_first_name || fallbackFirst,
      contact_last_name: tenant.contact_last_name || fallbackLast,
      contact_email: tenant.contact_email || "",
      contact_phone: tenant.contact_phone || "",
      finops_client_id: tenant.finops_client_id || "",
    });
    setDomainInput("");
  };

  const handleSaveEdit = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    if (form.approved_email_domains.length === 0) return toast.error("At least one email domain is required");
    try {
      await api.put(`/tenants/${editingTenant.id}`, form);
      toast.success(`Client "${form.name}" updated`);
      // If linked to FinOps, ask about syncing
      if (form.finops_client_id) {
        setSyncPrompt({ tenantId: editingTenant.id, tenantName: form.name, direction: "to" });
      }
      setEditingTenant(null); resetForm(); loadTenants();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update client"); }
  };

  const handleSyncToFinops = async (tenantId) => {
    try {
      await api.put(`/tenants/${tenantId}/sync-to-finops`);
      toast.success("Updated in Financial Ops");
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    setSyncPrompt(null);
  };

  const handleSyncFromFinops = async (tenantId) => {
    try {
      await api.put(`/tenants/${tenantId}/sync-from-finops`);
      toast.success("Updated from Financial Ops");
      loadTenants();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    setSyncPrompt(null);
  };

  const handleArchive = async (tenantId) => {
    try { await api.put(`/tenants/${tenantId}/archive`); toast.success("Client archived"); loadTenants(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to archive"); }
  };

  const handleUnarchive = async (tenantId) => {
    try { await api.put(`/tenants/${tenantId}/unarchive`); toast.success("Client restored"); loadTenants(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to unarchive"); }
  };

  const handleDeleteTenant = async () => {
    if (!confirmDelete) return;
    try { await api.delete(`/tenants/${confirmDelete}`); toast.success("Client permanently deleted"); setConfirmDelete(null); loadTenants(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleRestrictUser = async (userId) => {
    try { await api.put(`/users/${userId}/restrict`); toast.success("User restricted"); loadTenants(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleActivateUser = async (userId) => {
    try { await api.put(`/users/${userId}/activate`); toast.success("User activated"); loadTenants(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteUser = async () => {
    if (!confirmDeleteUser) return;
    try { await api.delete(`/users/${confirmDeleteUser}`); toast.success("User deleted"); setConfirmDeleteUser(null); loadTenants(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete user"); }
  };

  const toggleFavorite = async (tenant) => {
    // Optimistic so the list re-sorts immediately.
    const next = !tenant.is_favorite;
    setTenants(prev => prev.map(t => t.id === tenant.id ? { ...t, is_favorite: next } : t));
    try {
      if (next) await api.post(`/tenants/${tenant.id}/favorite`);
      else await api.delete(`/tenants/${tenant.id}/favorite`);
    } catch (err) {
      // Revert on failure.
      setTenants(prev => prev.map(t => t.id === tenant.id ? { ...t, is_favorite: !next } : t));
      toast.error(err.response?.data?.detail || "Couldn't update favorite");
    }
  };

  const handleImportFinops = async () => {
    setImporting(true);
    try {
      const res = await api.post("/tenants/import-finops-clients");
      const { imported, skipped } = res.data;
      if (imported > 0) {
        toast.success(`Imported ${imported} client${imported !== 1 ? "s" : ""} from FinOps${skipped > 0 ? ` (${skipped} already existed)` : ""}`);
        loadTenants();
      } else {
        toast.info("No new clients to import — all FinOps clients already exist in Pulse");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const resetForm = () => { setForm({ name: "", approved_email_domains: [], company_address: "", city: "", state_region: "", postal_code: "", country: "", contact_first_name: "", contact_last_name: "", contact_email: "", contact_phone: "", finops_client_id: "" }); setDomainInput(""); };
  const [sortBy, setSortBy] = useState("name");

  if (user?.user_type !== "internal") {
    return <div className="page-enter p-6 sm:p-8 text-center py-16"><ShieldCheck size={48} className="mx-auto text-[#E5E5E5] mb-4" /><p className="text-[#A3A3A3] font-medium">Access restricted to Eyvo internal users</p></div>;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>;

  // Filter by status: "active" tab shows active/null, "archived" tab shows archived/inactive
  const activeTenants = tenants.filter(t => t.status !== "archived" && t.status !== "inactive");
  const archivedTenants = tenants.filter(t => t.status === "archived" || t.status === "inactive");
  const sortedTenants = [...(viewMode === "active" ? activeTenants : archivedTenants)].sort((a, b) => {
    // Favourites first — always, regardless of sort mode.
    if (!!a.is_favorite !== !!b.is_favorite) return a.is_favorite ? -1 : 1;
    if (sortBy === "user_count") return (b.user_count || 0) - (a.user_count || 0);
    if (sortBy === "status") return (a.status || "").localeCompare(b.status || "");
    if (sortBy === "expiry") {
      // Expiring soonest first; null expiry dates sink to the bottom.
      const ax = a.expiry_date ? new Date(a.expiry_date).getTime() : Number.POSITIVE_INFINITY;
      const bx = b.expiry_date ? new Date(b.expiry_date).getTime() : Number.POSITIVE_INFINITY;
      return ax - bx;
    }
    return (a.name || "").localeCompare(b.name || "");
  });
  const displayTenants = sortedTenants;

  return (
    <div className="page-enter p-6 sm:p-8 space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-start flex-wrap gap-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Clients</h1>
          <p className="text-sm text-[#525252] mt-1">{displayTenants.length} client organization{displayTenants.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none px-6 h-10" data-testid="create-client-button"><Plus size={16} weight="bold" className="mr-2" />New Client</Button>
            </DialogTrigger>
          <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg">
            <DialogHeader><DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Add Client Organization</DialogTitle></DialogHeader>
            <ClientForm form={form} setForm={setForm} domainInput={domainInput} setDomainInput={setDomainInput} onAddDomain={handleAddDomain} onRemoveDomain={handleRemoveDomain} onDomainKeyDown={handleDomainKeyDown} onSubmit={handleCreate} submitLabel="Create Client" />
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-3 items-center">
        <div className="flex border border-[#E5E5E5]">
          <button onClick={() => setViewMode("active")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${viewMode === "active" ? "bg-[#0055FF] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`} data-testid="tab-active-clients">Active ({activeTenants.length})</button>
          <button onClick={() => setViewMode("archived")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${viewMode === "archived" ? "bg-[#525252] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`} data-testid="tab-archived-clients">Archived ({archivedTenants.length})</button>
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="border border-[#E5E5E5] px-3 py-2 text-xs font-bold uppercase tracking-wider focus:outline-none focus:border-[#0055FF]" data-testid="clients-sort">
          <option value="name">Sort: Name A-Z</option>
          <option value="user_count">Sort: Most Users</option>
          <option value="status">Sort: Status</option>
          <option value="expiry">Sort: Expiring Soonest</option>
        </select>
      </div>

      <div className="bg-[#F0F7FF] border border-[#0055FF]/20 p-4 flex items-start gap-3" data-testid="tenant-isolation-notice">
        <ShieldCheck size={20} className="text-[#0055FF] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-[#0A0A0A]">Tenant Isolation Enforced</p>
          <p className="text-xs text-[#525252] mt-0.5">Each client can only see their own projects, tasks, documents, and communications. Only users with approved email domains can join a client's tenant.</p>
        </div>
      </div>

      {/* Client Cards */}
      <div className="bg-white border border-[#E5E5E5]">
        {/* Column header — mirrors the row grid below.
            CRITICAL: every column width is FIXED. Header and each data row
            are independent grid containers; if any column is `auto` and
            its content widths differ between rows (e.g. the actions
            column expands when 3 buttons render but the header just has
            the word "ACTIONS"), then the `fr` columns absorb different
            amounts of leftover space per row and the headers no longer
            align with their data. Fixed widths everywhere = guaranteed
            alignment. */}
        <div className="hidden md:grid grid-cols-[1.5rem_1.5rem_minmax(0,2fr)_minmax(0,1.5fr)_6rem_3.5rem_13rem_17.5rem] gap-3 items-center px-4 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">
          <span aria-hidden />
          <span aria-hidden />
          <span>Client</span>
          <span>Email Domain</span>
          <span>Status</span>
          <span className="justify-self-end">Users</span>
          <span>Expiration</span>
          <span className="justify-self-end">Actions</span>
        </div>
        {displayTenants.map((tenant) => {
          const isArchived = tenant.status === "archived";
          return (
          <div key={tenant.id} className={isArchived ? "opacity-75" : ""} data-testid={`client-card-${tenant.id}`}>
            <div className={`px-4 py-2 ${!isArchived ? "cursor-pointer hover:bg-[#FAFAFA] transition-colors" : ""}`} onClick={() => { if (!isArchived) navigate(`/clients/${tenant.id}`); }}>
              <div className="grid grid-cols-[1.5rem_1.5rem_minmax(0,2fr)_minmax(0,1.5fr)_6rem_3.5rem_13rem_17.5rem] gap-3 items-center">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(tenant); }}
                  className="p-1 -m-1 hover:bg-[#FFFBEB] transition-colors flex-shrink-0"
                  title={tenant.is_favorite ? "Remove from favourites" : "Mark as favourite"}
                  data-testid={`favorite-toggle-${tenant.id}`}
                >
                  <Star
                    size={14}
                    weight={tenant.is_favorite ? "fill" : "regular"}
                    className={tenant.is_favorite ? "text-[#F59E0B]" : "text-[#CBD5E1] hover:text-[#F59E0B]"}
                  />
                </button>
                {/* 2026-07 client-logo repair sprint — real logo when one
                    resolves, falling back to initials (never a raw
                    building icon or a broken-image glyph). */}
                <ClientLogo client={tenant} size="xs" testId={`client-logo-${tenant.id}`} />
                <h3 className="text-sm font-bold text-[#0A0A0A] truncate" data-testid={`client-name-${tenant.id}`} title={tenant.name}>{clientDisplayName(tenant)}</h3>
                <span className="text-[11px] text-[#525252] font-medium truncate">
                  {(tenant.approved_email_domains || []).length > 0
                    ? tenant.approved_email_domains.map(d => `@${d}`).join(", ")
                    : <span className="text-[#CBD5E1]">—</span>}
                </span>
                <Badge className={`text-[9px] font-bold uppercase px-1.5 py-0 rounded-none border-0 justify-self-start ${isArchived ? "bg-[#F5F5F5] text-[#A3A3A3]" : tenant.status === "inactive" ? "bg-[#FFFBEB] text-[#D97706]" : "bg-[#F0FDF4] text-[#16A34A]"}`}>{tenant.status}</Badge>
                <span className="text-[11px] text-[#A3A3A3] flex items-center gap-1 justify-self-end"><Users size={11} /> {tenant.user_count || 0}</span>
                <div className="justify-self-start">
                  {tenant.expiry_date
                    ? <ExpiryBadge expiryDate={tenant.expiry_date} />
                    : <span className="text-[10px] text-[#CBD5E1]">—</span>}
                </div>
                <div className="flex gap-1.5 justify-self-end items-center" onClick={e => e.stopPropagation()}>
                  {tenant.bcc_token && (
                    <button
                      onClick={async () => {
                        const addr = `bcc-${tenant.bcc_token}@replies.eyvopulse.com`;
                        try {
                          await navigator.clipboard.writeText(addr);
                          toast.success(`BCC for ${tenant.name} copied`);
                        } catch {
                          toast.error("Could not copy — clipboard blocked");
                        }
                      }}
                      className="p-1 text-[#525252] hover:text-[#D97706] hover:bg-[#FFFBEB] transition-colors flex-shrink-0"
                      data-testid={`row-copy-bcc-${tenant.id}`}
                      title="Copy BCC archive email for this client — paste into the Bcc field of your next outbound email to archive it under Externally Sent Emails."
                    >
                      <EnvelopeSimple size={14} />
                    </button>
                  )}
                  {isArchived ? (
                    hasRole("platform_admin") && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => handleUnarchive(tenant.id)} className="rounded-none border-[#E5E5E5] h-6 px-2 text-[10px] font-bold uppercase tracking-wider" data-testid={`unarchive-client-${tenant.id}`}><ArrowCounterClockwise size={11} className="mr-1" />Restore</Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirmDelete(tenant.id)} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] h-6 px-2 text-[10px] font-bold uppercase tracking-wider" data-testid={`delete-client-${tenant.id}`}><Trash size={11} className="mr-1" />Delete</Button>
                      </>
                    )
                  ) : (
                    <>
                      <Button variant="outline" size="sm" onClick={() => loadTenantTickets(tenant)} className="rounded-none border-[#0055FF] text-[#0055FF] hover:bg-[#F0F7FF] h-6 px-2 text-[10px] font-bold uppercase tracking-wider" data-testid={`view-tickets-${tenant.id}`}><Ticket size={11} className="mr-1" />Tickets</Button>
                      {hasRole("platform_admin") && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => handleArchive(tenant.id)} className="rounded-none border-[#E5E5E5] h-6 px-2 text-[10px] font-bold uppercase tracking-wider text-[#525252]" data-testid={`archive-client-${tenant.id}`}><Archive size={11} className="mr-1" />Archive</Button>
                          <Button variant="outline" size="sm" onClick={() => setConfirmDelete(tenant.id)} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] h-6 px-2 text-[10px] font-bold uppercase tracking-wider" data-testid={`delete-client-${tenant.id}`}><Trash size={11} className="mr-1" />Delete</Button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Users List - loaded on demand */}
              {tenant.users && tenant.users.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-2 flex items-center gap-1.5"><Users size={11} /> Client Users</p>
                  <div className="border border-[#E5E5E5]">
                    <div className="grid grid-cols-[1fr_1.5fr_auto_auto_auto_auto] gap-2 px-3 py-2 bg-[#FAFAFA] border-b border-[#E5E5E5]">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Name</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Email / Phone</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Role</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Status</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Alerts</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Actions</span>
                    </div>
                    {tenant.users.map((u) => (
                      <div key={u.id} className="grid grid-cols-[1fr_1.5fr_auto_auto_auto_auto] gap-2 px-3 py-2.5 border-b border-[#F5F5F5] last:border-0 items-center hover:bg-[#FAFAFA]" data-testid={`client-user-${u.id}`}>
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-[#E5E5E5] flex items-center justify-center text-[10px] font-bold text-[#525252] flex-shrink-0">{u.name?.charAt(0)?.toUpperCase() || "?"}</div>
                          <span className="text-sm font-medium text-[#0A0A0A] truncate">{u.name}</span>
                        </div>
                        <div className="text-xs text-[#525252] space-y-0.5">
                          <div className="flex items-center gap-1"><EnvelopeSimple size={11} className="text-[#A3A3A3]" />{u.email}</div>
                          {u.phone && <div className="flex items-center gap-1"><Phone size={11} className="text-[#A3A3A3]" />{u.phone}</div>}
                        </div>
                        <Badge className="text-[9px] font-bold uppercase px-1.5 py-0 rounded-none border-0 bg-[#F5F5F5] text-[#525252]">{u.role?.replace(/_/g, " ")}</Badge>
                        <Badge className={`text-[9px] font-bold uppercase px-1.5 py-0 rounded-none border-0 ${u.status === "active" ? "bg-[#F0FDF4] text-[#16A34A]" : "bg-[#FEF2F2] text-[#DC2626]"}`}>{u.status}</Badge>
                        <div className="flex gap-1">
                          {u.is_primary_contact && <Badge className="text-[9px] font-bold uppercase px-1.5 py-0 rounded-none border-0 bg-[#F0F7FF] text-[#0055FF]">Primary Contact</Badge>}
                          {u.receive_email_alerts && <Badge className="text-[8px] font-bold px-1 py-0 rounded-none border-0 bg-[#F5F5F5] text-[#525252]" title="Email alerts">Email</Badge>}
                          {u.receive_sms_alerts && <Badge className="text-[8px] font-bold px-1 py-0 rounded-none border-0 bg-[#F5F5F5] text-[#525252]" title="SMS alerts">SMS</Badge>}
                        </div>
                        <div className="flex gap-1">
                          {u.status === "active" ? (
                            <Button variant="ghost" size="sm" onClick={() => handleRestrictUser(u.id)} className="h-7 px-2 text-[#D97706] hover:text-[#B45309] hover:bg-[#FFFBEB]" title="Restrict user" data-testid={`restrict-user-${u.id}`}><Prohibit size={14} /></Button>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => handleActivateUser(u.id)} className="h-7 px-2 text-[#16A34A] hover:text-[#15803D] hover:bg-[#F0FDF4]" title="Activate user" data-testid={`activate-user-${u.id}`}><CheckCircle size={14} /></Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteUser(u.id)} className="h-7 px-2 text-[#DC2626] hover:text-[#B91C1C] hover:bg-[#FEF2F2]" title="Delete user" data-testid={`delete-user-${u.id}`}><Trash size={14} /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
        })}
      </div>

      {displayTenants.length === 0 && (
        <div className="text-center py-16"><Buildings size={48} className="mx-auto text-[#E5E5E5] mb-4" /><p className="text-[#A3A3A3] font-medium">{viewMode === "archived" ? "No archived clients" : "No clients yet"}</p></div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingTenant} onOpenChange={(open) => { if (!open) { setEditingTenant(null); resetForm(); } }}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg">
          <DialogHeader><DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Edit Client: {clientDisplayName(editingTenant)}</DialogTitle></DialogHeader>
          <ClientForm form={form} setForm={setForm} domainInput={domainInput} setDomainInput={setDomainInput} onAddDomain={handleAddDomain} onRemoveDomain={handleRemoveDomain} onDomainKeyDown={handleDomainKeyDown} onSubmit={handleSaveEdit} submitLabel="Save Changes" />
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Tenant */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-sm">
          <DialogHeader><DialogTitle className="font-bold text-[#DC2626]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Delete Client Permanently?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#525252]">This will permanently delete the client organization, all its projects, tasks, users, and communications. This action cannot be undone.</p>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-none h-10" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button className="flex-1 rounded-none h-10 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold" onClick={handleDeleteTenant} data-testid="confirm-delete-client">Delete Forever</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete User */}
      <Dialog open={!!confirmDeleteUser} onOpenChange={() => setConfirmDeleteUser(null)}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-sm">
          <DialogHeader><DialogTitle className="font-bold text-[#DC2626]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Delete User?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#525252]">This will permanently remove this user and all their data. This action cannot be undone.</p>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-none h-10" onClick={() => setConfirmDeleteUser(null)}>Cancel</Button>
            <Button className="flex-1 rounded-none h-10 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold" onClick={handleDeleteUser} data-testid="confirm-delete-user">Delete User</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sync Confirmation Dialog */}
      <Dialog open={!!syncPrompt} onOpenChange={() => setSyncPrompt(null)}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-md" data-testid="sync-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {syncPrompt?.direction === "to" ? "Update Financial Ops?" : "Sync from Financial Ops?"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {syncPrompt?.direction === "to" ? (
              <>
                <p className="text-sm text-[#525252]">
                  You've updated <strong>"{syncPrompt?.tenantName}"</strong> in the Project System. Would you like to push these changes to Financial Ops?
                </p>
                <p className="text-xs text-[#A3A3A3]">This will update the contact name, address, phone, and email in Financial Ops to match what you've just saved here.</p>
                <div className="flex gap-3">
                  <Button onClick={() => handleSyncToFinops(syncPrompt.tenantId)} className="flex-1 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold rounded-none h-10" data-testid="sync-to-finops-yes">Yes, Update FinOps</Button>
                  <Button variant="outline" onClick={() => setSyncPrompt(null)} className="flex-1 rounded-none h-10 border-[#E5E5E5] font-bold" data-testid="sync-to-finops-no">No, Keep Separate</Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-[#525252]">
                  Pull the latest data for <strong>"{syncPrompt?.tenantName}"</strong> from Financial Ops? This will overwrite the name, address, phone, and email in the Project System.
                </p>
                <div className="flex gap-3">
                  <Button onClick={() => handleSyncFromFinops(syncPrompt.tenantId)} className="flex-1 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold rounded-none h-10" data-testid="sync-from-finops-yes">Yes, Pull from FinOps</Button>
                  <Button variant="outline" onClick={() => setSyncPrompt(null)} className="flex-1 rounded-none h-10 border-[#E5E5E5] font-bold" data-testid="sync-from-finops-no">Cancel</Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Support Tickets Dialog */}
      <Dialog open={!!ticketsTenant} onOpenChange={(open) => { if (!open) { setTicketsTenant(null); setSelectedTicket(null); setTenantTickets([]); } }}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="tickets-dialog">
          <DialogHeader>
            {selectedTicket ? (
              <div className="flex items-center gap-3">
                <button onClick={() => setSelectedTicket(null)} className="p-1 hover:bg-[#F5F5F5] transition-colors" data-testid="ticket-back-button"><ArrowLeft size={18} className="text-[#525252]" /></button>
                <div>
                  <DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {selectedTicket.ticket_number} — {selectedTicket.subject}
                  </DialogTitle>
                  <p className="text-xs text-[#A3A3A3] mt-0.5">{clientDisplayName(ticketsTenant)}</p>
                </div>
              </div>
            ) : (
              <div>
                <DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Support Tickets — {clientDisplayName(ticketsTenant)}
                </DialogTitle>
                <p className="text-xs text-[#A3A3A3] mt-0.5">{tenantTickets.length} ticket{tenantTickets.length !== 1 ? "s" : ""}</p>
              </div>
            )}
          </DialogHeader>

          {selectedTicket ? (
            /* Ticket Detail View */
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-none border-0 ${
                  selectedTicket.status === "open" ? "bg-[#FEF2F2] text-[#DC2626]" :
                  selectedTicket.status === "in_progress" ? "bg-[#FFFBEB] text-[#D97706]" :
                  selectedTicket.status === "resolved" ? "bg-[#F0FDF4] text-[#16A34A]" :
                  selectedTicket.status === "closed" ? "bg-[#F5F5F5] text-[#A3A3A3]" :
                  "bg-[#F5F5F5] text-[#525252]"
                }`}>{selectedTicket.status?.replace(/_/g, " ")}</Badge>
                <Badge className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-none border-0 ${
                  selectedTicket.priority === "critical" ? "bg-[#FEF2F2] text-[#DC2626]" :
                  selectedTicket.priority === "high" ? "bg-[#FFF7ED] text-[#EA580C]" :
                  selectedTicket.priority === "medium" ? "bg-[#FFFBEB] text-[#D97706]" :
                  "bg-[#F5F5F5] text-[#A3A3A3]"
                }`}>{selectedTicket.priority}</Badge>
                <span className="text-xs text-[#A3A3A3]">via {selectedTicket.source}</span>
                {selectedTicket.assigned_to_name && <span className="text-xs text-[#525252]">Assigned to {selectedTicket.assigned_to_name}</span>}
              </div>
              {selectedTicket.submitted_by_name && (
                <p className="text-xs text-[#525252]">Submitted by {selectedTicket.submitted_by_name} ({selectedTicket.submitted_by_email}) — {formatLocal(selectedTicket.created_at)}</p>
              )}
              {selectedTicket.description && (
                <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4">
                  <p className="text-sm text-[#525252] leading-relaxed whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>
              )}

              {/* Messages / Correspondence */}
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3 flex items-center gap-1.5">
                  <ChatCircleDots size={14} /> Correspondence ({selectedTicket.messages?.length || 0})
                </p>
                <div className="space-y-3">
                  {(selectedTicket.messages || []).map((msg) => (
                    <div key={msg.id} className={`p-3 border-l-2 ${
                      msg.is_internal_note ? "bg-[#FFFBEB] border-l-[#D97706]" :
                      msg.author_type === "internal" ? "bg-[#F0F7FF] border-l-[#0055FF]" :
                      "bg-white border border-[#E5E5E5] border-l-[#16A34A]"
                    }`} data-testid={`ticket-msg-${msg.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-[#0A0A0A]">{msg.author_name || "Unknown"}</span>
                        <Badge className={`text-[8px] font-bold uppercase px-1.5 py-0 rounded-none border-0 ${
                          msg.is_internal_note ? "bg-[#FFFBEB] text-[#D97706]" :
                          msg.author_type === "internal" ? "bg-[#F0F7FF] text-[#0055FF]" :
                          "bg-[#F0FDF4] text-[#16A34A]"
                        }`}>{msg.is_internal_note ? "Internal Note" : msg.author_type === "internal" ? "Eyvo" : "Client"}</Badge>
                        <span className="text-[10px] text-[#A3A3A3] ml-auto">{formatLocal(msg.created_at)}</span>
                      </div>
                      <p className="text-sm text-[#525252] whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  ))}
                  {(!selectedTicket.messages || selectedTicket.messages.length === 0) && (
                    <p className="text-xs text-[#A3A3A3] py-4 text-center">No correspondence yet</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Ticket List View */
            <div className="space-y-2 pt-2">
              {ticketsLoading ? (
                <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>
              ) : tenantTickets.length === 0 ? (
                <div className="text-center py-12"><Ticket size={40} className="mx-auto text-[#E5E5E5] mb-3" /><p className="text-sm text-[#A3A3A3]">No support tickets from this client</p></div>
              ) : (
                tenantTickets.map((ticket) => (
                  <div key={ticket.id} onClick={() => loadTicketDetail(ticket.id)}
                    className="flex items-center gap-4 p-3 border border-[#E5E5E5] cursor-pointer hover:bg-[#FAFAFA] transition-colors"
                    data-testid={`tenant-ticket-${ticket.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-[#0055FF]">{ticket.ticket_number}</span>
                        <span className="text-sm font-medium text-[#0A0A0A] truncate">{ticket.subject}</span>
                      </div>
                      <p className="text-xs text-[#A3A3A3] mt-0.5">
                        {ticket.submitted_by_name || "Unknown"} — {formatLocalDate(ticket.created_at)} — {ticket.message_count || 0} message{(ticket.message_count || 0) !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-none border-0 ${
                        ticket.priority === "critical" ? "bg-[#FEF2F2] text-[#DC2626]" :
                        ticket.priority === "high" ? "bg-[#FFF7ED] text-[#EA580C]" :
                        ticket.priority === "medium" ? "bg-[#FFFBEB] text-[#D97706]" :
                        "bg-[#F5F5F5] text-[#A3A3A3]"
                      }`}>{ticket.priority}</Badge>
                      <Badge className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-none border-0 ${
                        ticket.status === "open" ? "bg-[#FEF2F2] text-[#DC2626]" :
                        ticket.status === "in_progress" ? "bg-[#FFFBEB] text-[#D97706]" :
                        ticket.status === "resolved" ? "bg-[#F0FDF4] text-[#16A34A]" :
                        "bg-[#F5F5F5] text-[#A3A3A3]"
                      }`}>{ticket.status?.replace(/_/g, " ")}</Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClientForm({ form, setForm, domainInput, setDomainInput, onAddDomain, onRemoveDomain, onDomainKeyDown, onSubmit, submitLabel }) {
  const [finopsSearch, setFinopsSearch] = useState("");
  const [finopsResults, setFinopsResults] = useState([]);
  const [finopsLoading, setFinopsLoading] = useState(false);
  // Why a separate state rather than just an empty result list: an empty
  // list means "searched, found nothing". A failed request means we never
  // searched. Collapsing the two makes the dropdown say "No matches
  // found" when the lookup is unreachable — which is untrue, and sends
  // the user looking for a company that may well be there.
  const [finopsError, setFinopsError] = useState("");
  const [showFinops, setShowFinops] = useState(false);
  const [selectedFinops, setSelectedFinops] = useState(form.finops_client_id ? { contact_name: "Linked" } : null);
  const finopsRef = useRef(null);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (finopsRef.current && !finopsRef.current.contains(e.target)) setShowFinops(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const searchFinops = (val) => {
    setFinopsSearch(val);
    setShowFinops(true);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!val.trim() || val.trim().length < 2) { setFinopsResults([]); setFinopsError(""); return; }
    searchTimerRef.current = setTimeout(async () => {
      try {
        setFinopsLoading(true);
        const res = await api.get(`/tenants/finops-clients?q=${encodeURIComponent(val.trim())}`);
        setFinopsResults(res.data);
        setFinopsError("");
      } catch (err) {
        setFinopsResults([]);
        const d = err?.response?.data?.detail;
        setFinopsError(
          (typeof d === "object" && d?.message) ||
          (typeof d === "string" && d) ||
          "Financial Ops lookup is unavailable. Enter the company details below instead."
        );
      }
      finally { setFinopsLoading(false); }
    }, 300);
  };

  const selectFinopsContact = (contact) => {
    setSelectedFinops(contact);
    setFinopsSearch(contact.contact_name || "");
    setShowFinops(false);
    setForm(prev => ({
      ...prev,
      finops_client_id: contact.contact_id,
      name: contact.contact_name || prev.name,
      city: contact.po_city || prev.city,
      state_region: contact.po_region || prev.state_region,
      postal_code: contact.po_zip_code || prev.postal_code,
      country: contact.po_country || prev.country,
      contact_phone: contact.phone_number || prev.contact_phone,
      contact_email: contact.email_address || prev.contact_email,
    }));
  };

  const clearFinops = () => {
    setSelectedFinops(null);
    setFinopsSearch("");
    setForm(prev => ({ ...prev, finops_client_id: "" }));
  };

  return (
    <div className="space-y-5 pt-3 max-h-[70vh] overflow-y-auto pr-1">
      {/* FinOps Link */}
      <div className="space-y-1.5">
        <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#7C3AED]">Link to Financial Ops Contact</Label>
        <div className="relative" ref={finopsRef}>
          {selectedFinops ? (
            <div className="flex items-center gap-2 h-10 px-3 border border-[#7C3AED] bg-[#F5F0FF]">
              <span className="flex-1 text-sm font-medium text-[#0A0A0A] truncate">{selectedFinops.contact_name}</span>
              {selectedFinops.contact_type && <span className="text-[10px] font-bold uppercase text-[#7C3AED]">{selectedFinops.contact_type}</span>}
              <button onClick={clearFinops} className="p-0.5 hover:bg-[#FEF2F2] transition-colors" data-testid="finops-clear"><X size={14} className="text-[#DC2626]" /></button>
            </div>
          ) : (
            <>
              <input
                value={finopsSearch}
                onChange={(e) => searchFinops(e.target.value)}
                onFocus={() => { if (finopsSearch.length >= 2) setShowFinops(true); }}
                placeholder="Search Financial Ops contacts..."
                className="w-full h-10 px-3 text-sm border border-[#E5E5E5] bg-white focus:border-[#7C3AED] outline-none"
                data-testid="finops-search-input"
              />
              {showFinops && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E5E5] shadow-lg max-h-48 overflow-y-auto z-50" data-testid="finops-search-results">
                  {finopsLoading ? (
                    <div className="p-3 text-center text-xs text-[#A3A3A3]">Searching...</div>
                  ) : finopsError ? (
                    <div className="p-3 text-xs text-[#B45309] bg-[#FFFBEB] border-b border-[#FDE68A]" data-testid="finops-search-error">{finopsError}</div>
                  ) : finopsResults.length === 0 ? (
                    <div className="p-3 text-center text-xs text-[#A3A3A3]">{finopsSearch.length < 2 ? "Type at least 2 characters" : "No matches found"}</div>
                  ) : (
                    finopsResults.map(fc => (
                      <button key={fc.contact_id} onClick={() => selectFinopsContact(fc)}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#F5F0FF] transition-colors text-left border-b border-[#F5F5F5] last:border-0"
                        data-testid={`finops-result-${fc.contact_id}`}
                      >
                        <div>
                          <p className="text-sm font-medium text-[#0A0A0A]">{fc.contact_name}</p>
                          <p className="text-[10px] text-[#A3A3A3]">{[fc.po_city, fc.po_country].filter(Boolean).join(", ")}</p>
                        </div>
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${fc.contact_type === "customer" ? "bg-[#F0FDF4] text-[#16A34A]" : "bg-[#F5F5F5] text-[#525252]"}`}>{fc.contact_type || "contact"}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <p className="text-[10px] text-[#A3A3A3]">Search by company name to link to an existing Financial Ops contact, or leave blank for a new one.</p>
      </div>

      {/* Company Details */}
      <div className="space-y-1.5">
        <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Company Name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-[#E5E5E5] h-10" placeholder="e.g. Stanford University" data-testid="client-name-input" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Company Address</Label>
        <Input value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} className="rounded-none border-[#E5E5E5] h-10" placeholder="Street address" data-testid="client-address-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">City</Label>
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-none border-[#E5E5E5] h-10" placeholder="City" data-testid="client-city-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">State / Region</Label>
          <Input value={form.state_region} onChange={(e) => setForm({ ...form, state_region: e.target.value })} className="rounded-none border-[#E5E5E5] h-10" placeholder="State" data-testid="client-state-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Postal Code</Label>
          <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="rounded-none border-[#E5E5E5] h-10" placeholder="Postcode" data-testid="client-postcode-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Country</Label>
          <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-none border-[#E5E5E5] h-10" placeholder="Country" data-testid="client-country-input" />
        </div>
      </div>

      {/* PM Contact */}
      <div className="border-t border-[#E5E5E5] pt-4 space-y-3">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#0055FF]">Client Contact / Project Manager</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">First Name</Label>
            <Input value={form.contact_first_name || ""} onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })} className="rounded-none border-[#E5E5E5] h-10" placeholder="First" data-testid="client-contact-first-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Last Name</Label>
            <Input value={form.contact_last_name || ""} onChange={(e) => setForm({ ...form, contact_last_name: e.target.value })} className="rounded-none border-[#E5E5E5] h-10" placeholder="Last" data-testid="client-contact-last-name" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Contact Email</Label>
            <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-none border-[#E5E5E5] h-10" placeholder="email@company.com" data-testid="client-contact-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Contact Phone</Label>
            <Input type="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="rounded-none border-[#E5E5E5] h-10" placeholder="+1 555 000 0000" data-testid="client-contact-phone" />
          </div>
        </div>
      </div>

      {/* Email Domains */}
      <div className="border-t border-[#E5E5E5] pt-4 space-y-2">
        <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Approved Email Domains</Label>
        <p className="text-[10px] text-[#A3A3A3]">Only users with these email domains can join this client's tenant.</p>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] text-sm">@</span>
            <Input value={domainInput} onChange={(e) => setDomainInput(e.target.value)} onKeyDown={onDomainKeyDown} className="rounded-none border-[#E5E5E5] h-10 pl-7" placeholder="company.com" data-testid="domain-input" />
          </div>
          <Button type="button" onClick={onAddDomain} className="bg-[#0A0A0A] hover:bg-[#262626] text-white font-bold rounded-none h-10 px-4" data-testid="add-domain-button">Add</Button>
        </div>
        <div className="space-y-1.5 mt-2">
          {form.approved_email_domains.map((domain) => (
            <div key={domain} className="flex items-center justify-between bg-[#F5F5F5] border border-[#E5E5E5] px-3 py-2" data-testid={`domain-item-${domain}`}>
              <div className="flex items-center gap-2"><Globe size={14} className="text-[#0055FF]" /><span className="text-sm font-medium text-[#0A0A0A]">@{domain}</span></div>
              <button onClick={() => onRemoveDomain(domain)} className="p-1 hover:bg-[#FEF2F2] transition-colors" data-testid={`remove-domain-${domain}`}><X size={14} className="text-[#DC2626]" /></button>
            </div>
          ))}
          {form.approved_email_domains.length === 0 && <p className="text-xs text-[#A3A3A3] py-2 text-center border border-dashed border-[#E5E5E5] bg-[#FAFAFA]">No domains added yet. Add at least one domain.</p>}
        </div>
      </div>

      <Button onClick={onSubmit} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-10" data-testid="submit-client-button">{submitLabel}</Button>
    </div>
  );
}
